import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { BranchedStoryType, UpdateBranchedStoryData, update, getById } from "../../../../client/api/branchedStory";
import { QuestionCategory } from "common/build/prisma/client/client";
import { MappedStoryQuestionType } from "../../../../models/modelTypes";

// Type definitions for complex data updates
interface StoryPartUpdate {
    id: number;
    content?: string;
    partLabel?: string;
}

interface DecisionPointUpdate {
    id?: number;
    storyPartId: number;
    decisionText: string;
    order: number;
    branches: Array<{
        id?: number;
        branchLabel: string;
        choiceText: string;
        targetPartNumber: number;
        order: number;
    }>;
}

interface BranchedStoryEditContextType {
    originalStory: BranchedStoryType;
    currentStory: BranchedStoryType;
    hasChanges: boolean;
    loading: boolean;
    error: string | null;

    // Basic story field updates
    updateStoryField: <K extends keyof BranchedStoryType>(field: K, value: BranchedStoryType[K]) => void;

    // Complex data tracking
    editedParts: Map<number, Partial<StoryPartUpdate>>;
    editedDecisionPoints: Map<number, Partial<DecisionPointUpdate>>;
    editedQuestions: Map<number, Partial<MappedStoryQuestionType>>;

    // Complex data update methods
    updateStoryPart: (partId: number, updates: Partial<StoryPartUpdate>) => void;
    updateDecisionPoint: (pointId: number, updates: Partial<DecisionPointUpdate>) => void;
    updateQuestion: (questionId: number, updates: Partial<MappedStoryQuestionType>) => void;

    // Track which sections have changes
    hasPartChanges: boolean;
    hasDecisionChanges: boolean;
    hasQuestionChanges: boolean;

    // Story part and question management
    addStoryPart: () => void;
    addQuestion: (partId: number) => void;

    // Save and reset operations
    saveChanges: () => Promise<void>;
    resetChanges: () => void;
}

const BranchedStoryEditContext = createContext<BranchedStoryEditContextType | undefined>(undefined);

interface BranchedStoryEditProviderProps {
    children: ReactNode;
    initialStory: BranchedStoryType;
}

export const BranchedStoryEditProvider: React.FC<BranchedStoryEditProviderProps> = ({ children, initialStory }) => {
    const [originalStory, setOriginalStory] = useState<BranchedStoryType>(initialStory);
    const [currentStory, setCurrentStory] = useState<BranchedStoryType>(initialStory);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Complex data tracking
    const [editedParts, setEditedParts] = useState<Map<number, Partial<StoryPartUpdate>>>(new Map());
    const [editedDecisionPoints, setEditedDecisionPoints] = useState<Map<number, Partial<DecisionPointUpdate>>>(
        new Map(),
    );
    const [editedQuestions, setEditedQuestions] = useState<Map<number, Partial<MappedStoryQuestionType>>>(new Map());

    // Helper function to normalize null/undefined to empty string for comparison
    const normalizeString = (value: string | null | undefined): string => value || "";

    // Check if there are any basic metadata changes
    const hasMetadataChanges =
        normalizeString(originalStory.storyName) !== normalizeString(currentStory.storyName) ||
        normalizeString(originalStory.author) !== normalizeString(currentStory.author) ||
        normalizeString(originalStory.synopsis) !== normalizeString(currentStory.synopsis) ||
        normalizeString(originalStory.ageRange) !== normalizeString(currentStory.ageRange) ||
        normalizeString(originalStory.colour) !== normalizeString(currentStory.colour) ||
        normalizeString(originalStory.resources) !== normalizeString(currentStory.resources) ||
        normalizeString(originalStory.resourcesROI) !== normalizeString(currentStory.resourcesROI);

    // Check for complex data changes
    const hasNewParts = currentStory.StoryParts?.some((p) => p.id < 0) || false;
    const hasPartChanges = editedParts.size > 0 || hasNewParts;
    const hasNewDPs = Array.from(editedDecisionPoints.values()).some((dp) => !dp.id || dp.id >= 1000);
    const hasDecisionChanges = editedDecisionPoints.size > 0 || hasNewDPs;
    const hasNewQuestions =
        currentStory.StoryParts?.some((p) => p.MappedStoryQuestion?.some((q) => q.id && q.id < 0)) || false;
    const hasQuestionChanges = editedQuestions.size > 0 || hasNewQuestions;

    // Overall changes check
    const hasChanges = hasMetadataChanges || hasPartChanges || hasDecisionChanges || hasQuestionChanges;

    // Update a specific field in the story
    const updateStoryField = useCallback(<K extends keyof BranchedStoryType>(field: K, value: BranchedStoryType[K]) => {
        setCurrentStory((prevStory) => ({
            ...prevStory,
            [field]: value,
        }));
    }, []);

    // Update story part
    const updateStoryPart = useCallback((partId: number, updates: Partial<StoryPartUpdate>) => {
        setEditedParts((prevParts) => {
            const newParts = new Map(prevParts);
            const existingUpdate = newParts.get(partId) || { id: partId };
            newParts.set(partId, { ...existingUpdate, ...updates });
            return newParts;
        });
    }, []);

    // Update decision point
    const updateDecisionPoint = useCallback((pointId: number, updates: Partial<DecisionPointUpdate>) => {
        setEditedDecisionPoints((prevPoints) => {
            const newPoints = new Map(prevPoints);
            const existingUpdate = newPoints.get(pointId) || { id: pointId };
            newPoints.set(pointId, { ...existingUpdate, ...updates });
            return newPoints;
        });
    }, []);

    // Update question
    const updateQuestion = useCallback((questionId: number, updates: Partial<MappedStoryQuestionType>) => {
        setEditedQuestions((prevQuestions) => {
            const newQuestions = new Map(prevQuestions);
            const existingUpdate = newQuestions.get(questionId) || { id: questionId };
            newQuestions.set(questionId, { ...existingUpdate, ...updates });
            return newQuestions;
        });
    }, []);

    // Add a new story part (max 7 parts for branched stories)
    const addStoryPart = useCallback(() => {
        setCurrentStory((prev) => {
            const existingParts = prev.StoryParts || [];
            if (existingParts.length >= 7) return prev;

            const maxPart = existingParts.length > 0 ? Math.max(...existingParts.map((p) => p.part)) : 0;
            const newPart = {
                id: -Date.now(),
                part: maxPart + 1,
                content: "",
                partLabel: "",
                StoryDecisionPoints: [],
                MappedStoryQuestion: [],
            };
            return {
                ...prev,
                StoryParts: [...existingParts, newPart],
            };
        });
    }, []);

    // Add a new question to a story part (max 3 questions per part)
    const addQuestion = useCallback((partId: number) => {
        setCurrentStory((prev) => {
            const updatedParts = (prev.StoryParts || []).map((part) => {
                if (part.id !== partId) return part;
                const existingQs = part.MappedStoryQuestion || [];
                if (existingQs.length >= 3) return part;

                const newQ: Partial<MappedStoryQuestionType> = {
                    id: -Date.now(),
                    question: "",
                    category: "Open" as const,
                    order: existingQs.length + 1,
                    keywords: "",
                    prompt: "",
                    hint1: "",
                    hint2: "",
                    explaination: "",
                    prepGuide: "",
                    keywordDesc: "",
                };
                return {
                    ...part,
                    MappedStoryQuestion: [...existingQs, newQ],
                };
            });
            return { ...prev, StoryParts: updatedParts };
        });
    }, []);

    // Save changes to the backend
    const saveChanges = useCallback(async () => {
        if (!hasChanges) return;

        setLoading(true);
        setError(null);

        try {
            const updateData: UpdateBranchedStoryData = {
                storyName: currentStory.storyName,
                author: currentStory.author,
                synopsis: currentStory.synopsis,
                ageRange: currentStory.ageRange,
                colour: currentStory.colour,
                resources: currentStory.resources,
                resourcesROI: currentStory.resourcesROI,
                // Map themes to array of IDs if they exist
                themes: currentStory.Themes?.map((theme) => theme.id) || [],
            };

            // Include complex data if there are changes
            if (hasPartChanges) {
                updateData.storyParts = Array.from(editedParts.values()).filter(
                    (part) => part.id !== undefined && part.id > 0,
                ) as Array<{
                    id: number;
                    content?: string;
                    partLabel?: string;
                }>;
            }

            // Include new parts (negative IDs = added via addStoryPart)
            // Merge edited content from editedParts (user may have typed into the TextField)
            const newParts = currentStory.StoryParts?.filter((p) => p.id < 0) || [];
            if (newParts.length > 0) {
                updateData.newParts = newParts.map((p) => {
                    const edited = editedParts.get(p.id);
                    return {
                        part: p.part,
                        content: edited?.content ?? p.content ?? "",
                        partLabel: (edited?.partLabel ?? p.partLabel)?.substring(0, 20) || undefined,
                    };
                });
            }

            // Include new decision points (temp IDs >= 1000 from placeholder DPs)
            const newDPs = Array.from(editedDecisionPoints.values()).filter(
                (dp) => dp.storyPartId && dp.storyPartId > 0 && (!dp.id || dp.id >= 1000),
            );
            if (newDPs.length > 0) {
                updateData.newDecisionPoints = newDPs.map((dp) => ({
                    storyPartId: dp.storyPartId!,
                    decisionText: dp.decisionText || "",
                    order: dp.order || 1,
                    branches: (dp.branches || []).map((b) => ({
                        branchLabel: b.branchLabel || "A",
                        choiceText: b.choiceText || "",
                        targetPartNumber: b.targetPartNumber || 0,
                        order: b.order || 1,
                    })),
                }));
            }

            // Include new questions (negative IDs = added via addQuestion)
            const allNewQuestions: Array<{
                storyPartId: number;
                question: string;
                category: QuestionCategory;
                order: number;
                keywords?: string;
                prompt?: string;
                hint1?: string;
                explaination?: string;
                keywordDesc?: string;
                prepGuide?: string;
            }> = [];
            currentStory.StoryParts?.forEach((part) => {
                const newQs = (part.MappedStoryQuestion || []).filter((q) => q.id != undefined && q.id < 0);
                newQs.forEach((q) => {
                    // Check if edited version exists
                    const edited = q.id ? editedQuestions.get(q.id) : undefined;
                    allNewQuestions.push({
                        storyPartId: part.id > 0 ? part.id : 0, // Will be resolved by backend for new parts
                        question: edited?.question || q.question || "",
                        category: (edited?.category || q.category || "Open") as QuestionCategory,
                        order: q.order || -1, // fallback to keep typescript happy
                        keywords: edited?.keywords || q.keywords || "",
                        prompt: edited?.prompt || q.prompt || "",
                        hint1: edited?.hint1 || q.hint1 || "",
                        explaination: edited?.explaination || q.explaination || "",
                        keywordDesc: edited?.keywordDesc || q.keywordDesc || "",
                        prepGuide: edited?.prepGuide || q.prepGuide || "",
                    });
                });
            });
            if (allNewQuestions.length > 0) {
                updateData.newQuestions = allNewQuestions;
            }

            if (hasDecisionChanges) {
                updateData.decisionPoints = Array.from(editedDecisionPoints.values()).filter(
                    (point) => point.id !== undefined,
                ) as Array<{
                    id: number;
                    decisionText?: string;
                    order?: number;
                    branches?: Array<{
                        id: number;
                        branchLabel?: string;
                        choiceText?: string;
                        targetPartNumber?: number;
                        order?: number;
                    }>;
                }>;
            }

            if (hasQuestionChanges) {
                updateData.questions = Array.from(editedQuestions.values()).filter(
                    (question) => question.id !== undefined,
                ) as Array<{
                    id: number;
                    question?: string;
                    category?: QuestionCategory;
                    order?: number;
                    keywords?: string;
                    prompt?: string;
                    hint1?: string;
                    explaination?: string;
                    hint2?: string;
                    prepGuide?: string;
                    keywordDesc?: string;
                }>;
            }

            await update(currentStory.id, updateData);

            // Fetch the updated story to get the latest data
            const updatedStory = await getById(currentStory.id);

            // Update both current and original story to match the saved state
            setCurrentStory(updatedStory);
            setOriginalStory(updatedStory);

            // Clear the edited data maps
            setEditedParts(new Map());
            setEditedDecisionPoints(new Map());
            setEditedQuestions(new Map());
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save changes";
            setError(errorMessage);
            throw err; // Re-throw to let the caller handle it
        } finally {
            setLoading(false);
        }
    }, [
        currentStory,
        hasChanges,
        hasPartChanges,
        hasDecisionChanges,
        hasQuestionChanges,
        editedParts,
        editedDecisionPoints,
        editedQuestions,
    ]);

    // Reset changes to original state
    const resetChanges = useCallback(() => {
        setCurrentStory(originalStory);
        setEditedParts(new Map());
        setEditedDecisionPoints(new Map());
        setEditedQuestions(new Map());
        setError(null);
    }, [originalStory]);

    const contextValue: BranchedStoryEditContextType = {
        originalStory,
        currentStory,
        hasChanges,
        loading,
        error,
        updateStoryField,
        editedParts,
        editedDecisionPoints,
        editedQuestions,
        updateStoryPart,
        updateDecisionPoint,
        updateQuestion,
        hasPartChanges,
        hasDecisionChanges,
        hasQuestionChanges,
        addStoryPart,
        addQuestion,
        saveChanges,
        resetChanges,
    };

    return <BranchedStoryEditContext.Provider value={contextValue}>{children}</BranchedStoryEditContext.Provider>;
};

// Custom hook to use the context
export const useBranchedStoryEdit = (): BranchedStoryEditContextType => {
    const context = useContext(BranchedStoryEditContext);

    if (context === undefined) {
        throw new Error("useBranchedStoryEdit must be used within a BranchedStoryEditProvider");
    }

    return context;
};

// Display names for debugging (following design guide)
BranchedStoryEditProvider.displayName = "BranchedStoryEditProvider";
useBranchedStoryEdit.displayName = "useBranchedStoryEdit";
